import React, { useState, useEffect } from 'react';
import { familyArchersAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  User,
  Target,
  Building2,
  Calendar,
  Loader2,
  Save,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BOW_TYPES = ['Recurve', 'Compound', 'Barebow', 'Longbow', 'Traditional'];
const AGE_CLASSES = ['U10', 'U12', 'U14', 'U15', 'U18', 'U21', 'Open', 'Senior', 'Master'];

export default function FamilyArchers() {
  const { user } = useAuth();
  const [archers, setArchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingArcher, setEditingArcher] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    birth_year: '',
    bow_type: '',
    age_class: '',
    organization: '',
    member_id: '',
    notes: ''
  });

  useEffect(() => {
    if (user?.role === 'parent') {
      fetchArchers();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchArchers = async () => {
    try {
      const response = await familyArchersAPI.getAll();
      setArchers(response.data);
    } catch (error) {
      toast.error('Failed to load archers');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      birth_year: '',
      bow_type: '',
      age_class: '',
      organization: '',
      member_id: '',
      notes: ''
    });
    setEditingArcher(null);
  };

  const openEditDialog = (archer) => {
    setEditingArcher(archer);
    setFormData({
      name: archer.name || '',
      birth_year: archer.birth_year?.toString() || '',
      bow_type: archer.bow_type || '',
      age_class: archer.age_class || '',
      organization: archer.organization || '',
      member_id: archer.member_id || '',
      notes: archer.notes || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Archer name is required');
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: formData.name.trim(),
        birth_year: formData.birth_year ? parseInt(formData.birth_year) : null,
        bow_type: formData.bow_type || null,
        age_class: formData.age_class || null,
        organization: formData.organization.trim() || null,
        member_id: formData.member_id.trim() || null,
        notes: formData.notes.trim() || null
      };

      if (editingArcher) {
        await familyArchersAPI.update(editingArcher.id, data);
        toast.success('Archer updated successfully');
      } else {
        await familyArchersAPI.add(data);
        toast.success('Archer added to family');
      }

      setDialogOpen(false);
      resetForm();
      fetchArchers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save archer';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (archer) => {
    if (!window.confirm(`Remove ${archer.name} from your family? Tournament history will be preserved.`)) {
      return;
    }

    try {
      await familyArchersAPI.delete(archer.id);
      setArchers(archers.filter(a => a.id !== archer.id));
      toast.success('Archer removed from family');
    } catch (error) {
      toast.error('Failed to remove archer');
    }
  };

  // Non-parent users see a message
  if (user?.role !== 'parent') {
    return (
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Family Archers</h1>
          <p className="text-slate-600 mt-1">Manage archers in your family</p>
        </div>
        
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            This feature is only available for Parent accounts. Parent accounts can register and manage multiple archers in their family.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary" />
          <p className="text-slate-600">Loading archers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="family-archers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Family Archers</h1>
          <p className="text-slate-600 mt-1">
            Manage archers in your family ({archers.length} archer{archers.length !== 1 ? 's' : ''})
          </p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-archer-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Archer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingArcher ? 'Edit Archer' : 'Add New Archer'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label>Archer Name *</Label>
                <Input
                  placeholder="Full name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="archer-name-input"
                />
              </div>

              {/* Birth Year & Age Class */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Birth Year</Label>
                  <Input
                    type="number"
                    placeholder="e.g., 2010"
                    value={formData.birth_year}
                    onChange={(e) => setFormData({ ...formData, birth_year: e.target.value })}
                    min="1990"
                    max={new Date().getFullYear()}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Age Class</Label>
                  <Select
                    value={formData.age_class}
                    onValueChange={(value) => setFormData({ ...formData, age_class: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      {AGE_CLASSES.map((cls) => (
                        <SelectItem key={cls} value={cls}>{cls}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Bow Type */}
              <div className="space-y-2">
                <Label>Bow Type</Label>
                <Select
                  value={formData.bow_type}
                  onValueChange={(value) => setFormData({ ...formData, bow_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select bow type" />
                  </SelectTrigger>
                  <SelectContent>
                    {BOW_TYPES.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Organization */}
              <div className="space-y-2">
                <Label>Club/Organization</Label>
                <Input
                  placeholder="e.g., Elite Archery Club"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                />
              </div>

              {/* Member ID */}
              <div className="space-y-2">
                <Label>Member ID</Label>
                <Input
                  placeholder="USA Archery or organization member ID"
                  value={formData.member_id}
                  onChange={(e) => setFormData({ ...formData, member_id: e.target.value })}
                />
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  placeholder="Any additional notes about this archer..."
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setDialogOpen(false);
                resetForm();
              }}>
                Cancel
              </Button>
              <Button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving}
                data-testid="save-archer-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {editingArcher ? 'Update Archer' : 'Add Archer'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Info Card */}
      <Alert className="border-blue-200 bg-blue-50">
        <Users className="w-4 h-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Add your family archers here. When registering for tournaments, you'll be able to select which archer is attending.
          Each archer can have their own organization membership and schedule.
        </AlertDescription>
      </Alert>

      {/* Archers List */}
      {archers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {archers.map((archer) => (
            <Card key={archer.id} className="card-base" data-testid={`archer-card-${archer.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{archer.name}</h3>
                      {archer.age_class && (
                        <Badge variant="secondary" className="text-xs mt-1">
                          {archer.age_class}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(archer)}
                      className="h-8 w-8"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(archer)}
                      className="h-8 w-8 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  {archer.bow_type && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Target className="w-4 h-4 text-slate-400" />
                      <span>{archer.bow_type}</span>
                    </div>
                  )}
                  {archer.organization && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Building2 className="w-4 h-4 text-slate-400" />
                      <span>{archer.organization}</span>
                    </div>
                  )}
                  {archer.birth_year && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Calendar className="w-4 h-4 text-slate-400" />
                      <span>Born {archer.birth_year}</span>
                    </div>
                  )}
                  {archer.member_id && (
                    <div className="flex items-center gap-2 text-slate-600">
                      <Badge variant="outline" className="text-xs">
                        ID: {archer.member_id}
                      </Badge>
                    </div>
                  )}
                </div>

                {archer.notes && (
                  <p className="mt-3 text-sm text-slate-500 italic border-t pt-3">
                    {archer.notes}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="card-base">
          <CardContent className="py-12 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No archers added yet</h3>
            <p className="text-slate-500 mb-4 max-w-md mx-auto">
              Add your family archers to start tracking their tournaments and schedules individually.
            </p>
            <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add First Archer
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
